/** @param {HTMLElement} root */
function syncTabs(root) {
  const navScroll = root.querySelector('.product-tabs__nav-scroll');
  let visibleCount = 0;

  root.querySelectorAll('[data-tab-trigger]').forEach((trigger) => {
    if (!(trigger instanceof HTMLButtonElement)) return;

    const panel = root.querySelector(`[data-tab-panel="${trigger.dataset.tabTrigger}"]`);
    if (!(panel instanceof HTMLElement)) return;

    const label = panel.getAttribute('data-tab-label');
    if (label) trigger.textContent = label;

    const hideWhenEmpty = panel.getAttribute('data-tab-hide-when-empty') === 'true';
    const hasMetafieldValue = panel.getAttribute('data-tab-metafield-has-value') === 'true';
    const shouldHide = hideWhenEmpty && !hasMetafieldValue;

    trigger.hidden = shouldHide;
    panel.hidden = shouldHide;

    if (!shouldHide) visibleCount += 1;
  });

  if (navScroll instanceof HTMLElement) {
    navScroll.style.setProperty('--tab-count', String(Math.max(visibleCount, 1)));
  }

  const section = root.closest('.product-tabs-section');
  if (section instanceof HTMLElement) {
    section.hidden = visibleCount === 0;
  }

  return visibleCount;
}

export class ProductTabsComponent extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  tabs = [];

  /** @type {HTMLElement[]} */
  panels = [];

  /** @type {number} */
  active = 0;

  connectedCallback() {
    syncTabs(this);

    this.tabs = [...this.querySelectorAll('[data-tab-trigger]')].flatMap((element) =>
      element instanceof HTMLButtonElement && !element.hidden ? [element] : []
    );
    this.panels = [...this.querySelectorAll('[data-tab-panel]')].flatMap((element) =>
      element instanceof HTMLElement && !element.hidden ? [element] : []
    );
    if (!this.tabs.length) return;

    this.active = 0;
    this.select(0);

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-tab-trigger]');
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab) || tab.hidden) return;

    const index = this.tabs.indexOf(tab);
    if (index !== -1) this.select(index);
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement) || !target.hasAttribute('data-tab-trigger')) return;

    const index = this.tabs.indexOf(target);
    if (index === -1) return;

    /** @type {Record<string, number>} */
    const offsets = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -index,
      End: this.tabs.length - 1 - index,
    };

    const offset = offsets[event.key];
    if (offset === undefined) return;

    event.preventDefault();
    const next = (index + offset + this.tabs.length) % this.tabs.length;
    const nextTab = this.tabs[next];
    if (!nextTab) return;

    nextTab.focus();
    this.select(next);
  }

  /** @param {number} index */
  select(index) {
    if (index < 0 || index >= this.tabs.length) return;

    this.active = index;
    const activeTab = this.tabs[index];
    const activePanelId = activeTab?.dataset.tabTrigger;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.tabPanel === activePanelId;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('inert', !isActive);
      panel.tabIndex = isActive ? 0 : -1;

      if (isActive && !prefersReducedMotion) {
        const inner = panel.querySelector('.product-tabs__panel-inner');
        if (inner instanceof HTMLElement) {
          inner.style.animation = 'none';
          void inner.offsetHeight;
          inner.style.removeProperty('animation');
        }
      }
    });
  }
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
}
